import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useContext, useMemo } from 'react';
import { csmAiTokens } from '../../common/constant';
import { getContrastYIQ } from '../../common/utils/get-contrast';
import { useGenerateThemeBranding } from './use-theme-styles';
export var ThemeContext = /*#__PURE__*/createContext(undefined);
export var useTheme = function useTheme() {
  var context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
/**
 * ThemeProvider component for applying consistent theming to child components.
 *
 * This provider sets up a theme context and injects CSS custom properties for styling its children.
 * It is intended for use by external consumers who want to customize the appearance of UI components
 * according to their brand or product requirements.
 *
 * Usage:
 * ```tsx
 * <ThemeProvider themeConfig={customTheme}>
 *   <YourComponent />
 * </ThemeProvider>
 * ```
 *
 * @param {object} props - The props for ThemeProvider.
 * @param {React.ReactNode} props.children - The child elements that will receive the theme context and styles.
 * @param {BrandingTheme} [props.themeConfig] - Optional custom theme configuration. If omitted, a default theme is used.
 *   - primaryColor: The main brand color (string, e.g., '#0052CC').
 *   - colorVibe: Controls the color palette and contrast ('light' | 'dark' | 'vibrant' | 'leadershipCircle').
 *   - radius: Controls the corner roundness ('sharp' | 'normal' | 'rounded').
 *   - space: Determines the spacing density ('cosy' | 'comfortable' | 'generous').
 *
 * @returns {JSX.Element} Themed context provider wrapping the children.
 */
export var ThemeProvider = function ThemeProvider(_ref) {
  var children = _ref.children,
    themeConfig = _ref.themeConfig;
  if (!themeConfig) {
    throw new Error('themeConfig is required for the CSM Branding to works');
  }
  var themeStyles = useGenerateThemeBranding(themeConfig);
  var tokenStyles = useMemo(function () {
    var _properties;
    var borderRadius = themeStyles.borderRadius,
      space = themeStyles.space,
      colorRamps = themeStyles.colorRamps;
    var iconColor = getContrastYIQ(themeConfig.primaryColor) === 'dark' ? '#FFFFFF' : '#000000';
    var properties = (_properties = {}, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_properties, csmAiTokens['color.100'], colorRamps.color100), csmAiTokens['color.200'], colorRamps.color200), csmAiTokens['color.300'], colorRamps.color300), csmAiTokens['color.400'], colorRamps.color400), csmAiTokens['color.500'], colorRamps.color500), csmAiTokens['color.600'], colorRamps.color600), csmAiTokens['color.700'], colorRamps.color700), csmAiTokens['color.800'], colorRamps.color800), csmAiTokens['color.900'], colorRamps.color900), csmAiTokens['color.1000'], colorRamps.color1000), _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_properties, csmAiTokens['color.icon'], iconColor), csmAiTokens['border.radius.lg'], borderRadius.lg), csmAiTokens['border.radius.md'], borderRadius.md), csmAiTokens['border.radius.sm'], borderRadius.sm), csmAiTokens['border.radius.container'], borderRadius.container), csmAiTokens['border.radius.widget'], borderRadius.widget), csmAiTokens['space.lg'], space.lg), csmAiTokens['space.md'], space.md), csmAiTokens['space.sm'], space.sm));
    return properties;
  }, [themeConfig.primaryColor, themeStyles]);
  var combineStyles = _objectSpread(_objectSpread({}, tokenStyles), {}, {
    // Use inline to ensure the wrapper won't mess with the style calculation
    // of the children (e.g. height calculation of the children)
    display: 'inline'
  });
  return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
    value: themeStyles
  }, /*#__PURE__*/React.createElement("div", {
    style: combineStyles
  }, children));
};